// Row-limiting clause classifier — every caret from the `LIMIT` / `OFFSET` /
// `FETCH` keyword to the end of the statement.
//
// Split out of `nextKeywordSlot.ts` (which owns the clause tails and calls in
// here) to keep each classifier module readable. Same CONFIDENT-ONLY contract:
// return `null` unless the text scan is clearly at the named boundary, because
// `null` means "fall back to the resolver's full emission". PURE text scan — no
// parse, no async, safe on the hot path.

import type { NextKeywordSlot } from './legalNextKeywords';
import { barewordTokens, slotScanEnd, type ScanOptions } from './slotTextScan';

// ─── row-limiting clause (FETCH / OFFSET / LIMIT) ────────────────────────────

/** Keywords that open a row-limiting / paging clause. `TOP` is listed so the
 *  backward anchor search stops on it, but it is REFUSED below: the clause
 *  machine names `SELECT TOP 10 ▮` a 'limit-offset' position too, and there the
 *  legal continuation is a projection (a column), not a paging keyword. */
const ROW_LIMIT_ANCHORS: ReadonlySet<string> = new Set(['FETCH', 'OFFSET', 'LIMIT', 'TOP']);

/** `FETCH { FIRST | NEXT }` — the two row-limiting directions. */
const FETCH_DIRECTIONS: ReadonlySet<string> = new Set(['FIRST', 'NEXT']);

/** `{ ROW | ROWS }` — the row-count unit, singular or plural. */
const ROW_UNITS: ReadonlySet<string> = new Set(['ROW', 'ROWS']);

/**
 * Names the sub-position inside a row-limiting clause (`FETCH …` / `OFFSET …` /
 * `LIMIT …`). Every position in that clause is keyword-or-integer only — a
 * column or table name is never legal there — so a confident classification
 * lets the resolver replace the schema flood with the paging keywords.
 *
 * Unlike the other classifiers here this one also fires MID-WORD (`FETCH fir▮`),
 * classifying from the tokens BEFORE the partial word: the paging clause has no
 * identifier position for a half-typed word to belong to, so there is nothing to
 * misread, and the keywords are most wanted exactly while they are being typed.
 */
export function classifyRowLimit(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlot | null {
  const scanEnd = slotScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;

  const tokens = barewordTokens(text, statementStart, scanEnd, opts);
  let anchorIdx = -1;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    if (ROW_LIMIT_ANCHORS.has(tokens[i]!.word)) {
      anchorIdx = i;
      break;
    }
  }
  if (anchorIdx === -1) return null;
  const anchor = tokens[anchorIdx]!.word;
  if (anchor === 'TOP') {
    // `SELECT TOP ▮` takes an integer (or a parenthesized expression); the
    // projection resumes only after it, so this one caret suppresses and the
    // rest of the TOP clause keeps the select-list emission.
    return tokens.length === anchorIdx + 1 ? 'at-top-count' : null;
  }

  // `FETCH ▮` is ALSO the cursor-fetch statement in PL/SQL (`FETCH c INTO v`)
  // and T-SQL (`FETCH NEXT FROM cur`), where a cursor name follows and paging
  // keywords would be wrong. The paging clause only ever trails a query, so
  // require a SELECT ahead of the anchor within the statement.
  let sawSelect = false;
  for (let i = 0; i < anchorIdx; i += 1) {
    if (tokens[i]!.word === 'SELECT') {
      sawSelect = true;
      break;
    }
  }
  if (!sawSelect) return null;

  const tail = tokens.slice(anchorIdx + 1).map((t) => t.word);
  const slot =
    anchor === 'FETCH' ? fetchTailSlot(tail)
    : anchor === 'OFFSET' ? offsetTailSlot(tail)
    : limitTailSlot(tail, countCommas(text, tokens[anchorIdx]!.start, scanEnd));
  return slot;
}

/** Walks the token tail after `FETCH` against
 *  `{ FIRST | NEXT } [ n [ PERCENT ] ] { ROW | ROWS } { ONLY | WITH TIES }`. */
function fetchTailSlot(tail: readonly string[]): NextKeywordSlot | null {
  let i = 0;
  if (i === tail.length) return 'after-fetch';
  if (!FETCH_DIRECTIONS.has(tail[i]!)) return null;
  i += 1;
  if (i === tail.length) return 'after-fetch-direction';
  if (isIntegerWord(tail[i]!)) {
    i += 1;
    if (i === tail.length) return 'after-fetch-count';
    if (tail[i] === 'PERCENT') {
      i += 1;
      // `n PERCENT` completes the quantity — `ROW`/`ROWS` follows, same as a
      // bare direction.
      if (i === tail.length) return 'after-fetch-direction';
    }
  }
  if (!ROW_UNITS.has(tail[i]!)) return null;
  i += 1;
  if (i === tail.length) return 'after-fetch-rows';
  // `… ROWS ONLY ▮` / `… ROWS WITH TIES ▮` — the clause is finished.
  if (tail[i] === 'ONLY' && i + 1 === tail.length) return 'after-row-limit-clause';
  if (tail[i] === 'WITH' && tail[i + 1] === 'TIES' && i + 2 === tail.length) {
    return 'after-row-limit-clause';
  }
  return null;
}

/** Walks the token tail after `OFFSET` against `n [ { ROW | ROWS } ]`. */
function offsetTailSlot(tail: readonly string[]): NextKeywordSlot | null {
  if (tail.length === 0) return 'at-offset-count';
  if (!isIntegerWord(tail[0]!)) return null;
  if (tail.length === 1) return 'after-offset-count';
  if (tail.length === 2 && ROW_UNITS.has(tail[1]!)) return 'after-offset-rows';
  return null;
}

/** Walks the token tail after `LIMIT` against `n` — or MySQL's two-argument
 *  `LIMIT m, n`, which `commaCount` tells apart from `LIMIT m ▮` (the tail is
 *  the single token `m` either way). */
function limitTailSlot(tail: readonly string[], commaCount: number): NextKeywordSlot | null {
  if (commaCount > 1) return null;
  if (commaCount === 1) {
    // `LIMIT m, ▮` → the row count; `LIMIT m, n ▮` → the clause is complete.
    return tail.length === 1 ? 'at-limit-count' : null;
  }
  if (tail.length === 0) return 'at-limit-count';
  return tail.length === 1 && isIntegerWord(tail[0]!) ? 'after-limit-count' : null;
}

/** True for a bare integer literal token (the row count / offset count). */
function isIntegerWord(word: string): boolean {
  return /^[0-9]+$/.test(word);
}

/** Commas between `from` and `to` — distinguishes MySQL's `LIMIT m, n` from
 *  `LIMIT m`, which tokenize identically once punctuation is dropped. */
function countCommas(text: string, from: number, to: number): number {
  let n = 0;
  for (let i = from; i < to; i += 1) if (text[i] === ',') n += 1;
  return n;
}
